import { FlockProfileTrait } from './flock-profile-types';

export type FlockProfileDisplayTrait = {
  trait: FlockProfileTrait;
  traitIndex: number;
  displayLabel: string;
  subgroupLabel?: string;
  subgroupCode?: string;
};

const normaliseWhitespace = (value: string): string => value.trim().replace(/\s+/g, ' ');
const stopWords = new Set(['and', 'of', 'the', 'a', 'an', 'for', 'to', 'in', 'on']);
const wordCodeMap: { [word: string]: string[] } = {
  weight: ['WT', 'W'],
};
const enableSubgroupLabels = false;

type LabelSuffix = {
  removeLabel: string;
  subgroupLabel: string;
  subgroupCode: string;
};

const getWordCodes = (word: string): string[] => {
  const normalisedWord = word.toLocaleLowerCase();

  if (stopWords.has(normalisedWord)) {
    return [];
  }

  return wordCodeMap[normalisedWord] || [word.charAt(0).toLocaleUpperCase()];
};

const getCodeCombinations = (wordCodes: string[][]): string[] => (
  wordCodes.reduce<string[]>((combinations, codes) => (
    combinations.reduce<string[]>((nextCombinations, combination) => (
      nextCombinations.concat(codes.map((code) => `${combination}${code}`))
    ), [])
  ), [''])
    .filter((combination) => combination.length > 0)
);

const getMatchingCode = (wordCodes: string[][], code: string): string | undefined => (
  getCodeCombinations(wordCodes)
    .sort((a, b) => b.length - a.length)
    .find((combination) => code.endsWith(combination))
);

const getSuffixes = (label: string, code: string): LabelSuffix[] => {
  const words = normaliseWhitespace(label).split(' ').filter((word) => word.length > 0);
  const suffixes: LabelSuffix[] = [];
  const normalisedCode = code.toLocaleUpperCase();

  for (let wordCount = 1; wordCount < words.length; wordCount += 1) {
    const suffixWords = words.slice(words.length - wordCount);
    const codedWords = suffixWords
      .map((word) => ({ word, codes: getWordCodes(word) }))
      .filter((word) => word.codes.length > 0);

    for (let startIndex = 0; startIndex < codedWords.length; startIndex += 1) {
      const matchingWords = codedWords.slice(startIndex);
      const subgroupCode = getMatchingCode(matchingWords.map((word) => word.codes), normalisedCode);

      if (subgroupCode) {
        const firstMatchingWordIndex = suffixWords.findIndex((word) => word === matchingWords[0].word);
        suffixes.push({
          removeLabel: suffixWords.join(' '),
          subgroupLabel: suffixWords.slice(firstMatchingWordIndex).join(' '),
          subgroupCode,
        });
        break;
      }
    }
  }

  return suffixes;
};

const removeSuffix = (label: string, suffix: string): string => {
  const normalisedLabel = normaliseWhitespace(label);
  const normalisedSuffix = normaliseWhitespace(suffix);

  if (normalisedLabel === normalisedSuffix) {
    return normalisedLabel;
  }

  if (!normalisedLabel.endsWith(` ${normalisedSuffix}`)) {
    return normalisedLabel;
  }

  return normalisedLabel.slice(0, normalisedLabel.length - normalisedSuffix.length).trim();
};

export const getDisplayTraits = (
  traits: { trait: FlockProfileTrait; traitIndex: number }[],
): FlockProfileDisplayTrait[] => {
  if (!enableSubgroupLabels) {
    return traits.map(({ trait, traitIndex }) => ({
      trait,
      traitIndex,
      displayLabel: trait.label,
    }));
  }

  const suffixCounts = new Map<string, number>();
  const suffixesByTraitIndex = new Map<number, LabelSuffix[]>();

  traits.forEach(({ trait, traitIndex }) => {
    const suffixes = getSuffixes(trait.label, trait.code);
    suffixesByTraitIndex.set(traitIndex, suffixes);

    suffixes.forEach((suffix) => {
      const key = `${suffix.subgroupCode}:${suffix.subgroupLabel}`;
      suffixCounts.set(key, (suffixCounts.get(key) || 0) + 1);
    });
  });

  return traits.map(({ trait, traitIndex }) => {
    const matchingSuffix = (suffixesByTraitIndex.get(traitIndex) || [])
      .filter((suffix) => (suffixCounts.get(`${suffix.subgroupCode}:${suffix.subgroupLabel}`) || 0) > 1)
      .sort((a, b) => b.subgroupCode.length - a.subgroupCode.length)[0];

    if (!matchingSuffix) {
      return {
        trait,
        traitIndex,
        displayLabel: trait.label,
      };
    }

    return {
      trait,
      traitIndex,
      displayLabel: removeSuffix(trait.label, matchingSuffix.removeLabel),
      subgroupLabel: matchingSuffix.subgroupLabel,
      subgroupCode: matchingSuffix.subgroupCode,
    };
  });
};
